'use client';

import { useEffect } from 'react';

import {
  useGetCargoCitiesQuery,
  useGetCargoDistrictsQuery,
  useGetCargoTownsQuery,
} from '@/services/cargoApi';
import { CARGO_ADDRESS_LINE_MAX } from '@/types/cargo';
import { Textarea } from '@holistic/components';
import { skipToken } from '@reduxjs/toolkit/query/react';

import type { CargoAddress, CargoLocation } from '@/types/cargo';

import CargoLocationSelect from './cargo-location-select';

export type AddressErrors = {
  city?: string;
  town?: string;
  addressLine?: string;
};

type AddressFieldsProps = {
  address: CargoAddress;
  errors: AddressErrors;
  isOnlyIstanbul?: boolean;
  onCityChange: (city: CargoLocation | null) => void;
  onTownChange: (town: CargoLocation | null) => void;
  onDistrictChange: (district: CargoLocation | null) => void;
  onAddressLineChange: (value: string) => void;
  onClearError: (key: keyof AddressErrors) => void;
};

/**
 * Cascade adres seçici: her seviye yalnızca üstü seçildiğinde istek atar
 * (`skipToken`) — il seçilmeden ilçe, ilçe seçilmeden semt listesi çekilmez.
 */
const AddressFields = ({
  address,
  errors,
  isOnlyIstanbul,
  onCityChange,
  onTownChange,
  onDistrictChange,
  onAddressLineChange,
  onClearError,
}: AddressFieldsProps) => {
  const { data: cities, isFetching: isCitiesLoading } =
    useGetCargoCitiesQuery();
  const { data: towns, isFetching: isTownsLoading } = useGetCargoTownsQuery(
    address.city?.id ?? skipToken
  );
  const { data: districts, isFetching: isDistrictsLoading } =
    useGetCargoDistrictsQuery(address.town?.id ?? skipToken);

  const availableCities = isOnlyIstanbul
    ? cities?.filter(
        (c) =>
          c.name.toLowerCase().includes('istanbul') ||
          c.name.toLowerCase().includes('i̇stanbul')
      )
    : cities;

  // TV / Özel Saha Teslimatı için il otomatik olarak İstanbul seçilir.
  useEffect(() => {
    if (isOnlyIstanbul && availableCities && availableCities.length > 0) {
      const istanbul = availableCities[0];
      if (!address.city || address.city.id !== istanbul.id) {
        onCityChange(istanbul);
      }
    }
  }, [isOnlyIstanbul, availableCities, address.city, onCityChange]);

  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        <CargoLocationSelect
          label="İl"
          placeholder="İl seçin"
          lockedText="İl seçin"
          options={availableCities}
          value={address.city}
          isLoading={isCitiesLoading}
          isLocked={false}
          error={errors.city}
          onSelect={(city) => {
            onClearError('city');
            onCityChange(city);
          }}
        />

        <CargoLocationSelect
          label="İlçe"
          placeholder="İlçe seçin"
          lockedText="Önce il seçin"
          options={towns}
          value={address.town}
          isLoading={isTownsLoading}
          isLocked={!address.city}
          error={errors.town}
          onSelect={(town) => {
            onClearError('town');
            onTownChange(town);
          }}
        />
      </div>

      {isOnlyIstanbul && (
        <p className="-mt-2 text-xs font-medium text-amber-600">
          * Özel saha ekibi teslimatımız şu anda yalnızca İstanbul ili genelinde
          geçerlidir.
        </p>
      )}

      <CargoLocationSelect
        label="Semt / Mahalle"
        placeholder="Semt seçin"
        lockedText="Önce ilçe seçin"
        options={districts}
        value={address.district}
        isLoading={isDistrictsLoading}
        isLocked={!address.town}
        optional
        onSelect={onDistrictChange}
      />

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-4">
          <label className="text-sm font-medium text-gray-700">
            Açık adres
          </label>
          <span className="shrink-0 text-[11px] text-gray-400">
            {address.addressLine.length}/{CARGO_ADDRESS_LINE_MAX}
          </span>
        </div>

        <Textarea
          value={address.addressLine}
          onChange={(e) => {
            onClearError('addressLine');
            onAddressLineChange(
              e.target.value.slice(0, CARGO_ADDRESS_LINE_MAX)
            );
          }}
          placeholder="Mahalle, sokak, bina ve daire no..."
          rows={3}
          error={errors.addressLine}
        />

        {!errors.addressLine && (
          <p className="text-xs leading-relaxed text-gray-400">
            Kat, daire ve varsa tarif bilgisini eklemeniz kuryenin işini
            kolaylaştırır.
          </p>
        )}
      </div>
    </>
  );
};

export default AddressFields;
